import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { ChecklistTemplatePreviewFields } from '@/components/ChecklistTemplatePreviewFields'
import { useChecklistTemplate } from '@/lib/hooks'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { ArrowLeft } from '@/components/icons'

export const Route = createFileRoute(
  '/_auth/operations/checklisttemplates/preview/$checklistId'
)({
  component: PreviewTemplateRoute,
})

function PreviewTemplateRoute() {
  const { checklistId } = Route.useParams()
  const { data: template, isLoading } = useChecklistTemplate(checklistId)
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()

  const title = `Preview${template?.name ? ` - ${template.name}` : ''}`

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/operations/checklisttemplates' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to checklists"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/operations" className="hover:underline">
          Operations
        </Link>
        <span className="text-content-primary-subtle"> / </span>
        <Link to="/operations/checklisttemplates" className="hover:underline">
          Checklists
        </Link>
        <span className="text-content-primary-subtle"> / {title}</span>
      </span>
    </span>
  )

  if (isLoading) {
    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title="Preview" breadcrumb={breadcrumb} />
          <DetailSkeleton rows={5} />
        </DesktopPage>
      )
    }
    return (
      <AppLayout topBar={<TopBarBackButton title="Preview" />} className="pt-6">
        <DetailSkeleton rows={5} />
      </AppLayout>
    )
  }

  const items = [...(template?.items ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title={title} breadcrumb={breadcrumb} />
        <ChecklistTemplatePreviewFields items={items} />
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title={title} />} className="pt-6">
      <ChecklistTemplatePreviewFields items={items} />
    </AppLayout>
  )
}
